import { isEnergyCardInvoiceId } from '../hookServices/energyCard';

/** Unlimited prepaid period used by charging UI (periodKwt === 220). */
const UNLIMITED_PERIOD_KWT = 220;

function isActiveChargingState(state) {
  return state === 'IN_PROGRESS' || state === 'WAITING';
}

function resolveSessionMetrics(item, lastJob, stationNumber) {
  const liveStation = lastJob?.stationNumber ?? stationNumber;
  const isLiveSession =
    Boolean(lastJob) && String(item.plugNumber) === String(liveStation) && isActiveChargingState(lastJob.state);

  const periodKwt = Number(isLiveSession ? lastJob.periodKwt : item.periodKwt) || 0;
  const chargedKwh = isLiveSession ? Number(lastJob.chargedWtH || 0) / 1000 : Number(item.energyWth || 0) / 1000;

  return { periodKwt, chargedKwh };
}

/**
 * Remaining prepaid cents for in-progress sessions: paid - spent.
 * Prefers live station metrics; falls back to history energyWth + periodKwt
 * so every screen uses the same leftover as the charging page.
 */
export function getInProgressPrepaidRemainingCents(historyData, stationStatus, stationNumber) {
  const items = Array.isArray(historyData) ? historyData.filter(item => isActiveChargingState(item?.state)) : [];

  let remainingCents = 0;
  const lastJob = stationStatus?.lastJob;

  for (const item of items) {
    if (isEnergyCardInvoiceId(item?.invoiceId)) {
      continue;
    }

    const paidCents = Number(item?.priceInCents) || 0;
    if (paidCents <= 0) continue;

    const { periodKwt, chargedKwh } = resolveSessionMetrics(item, lastJob, stationNumber);

    if (!periodKwt || periodKwt === UNLIMITED_PERIOD_KWT) {
      // Unlimited: OnlineStation treats paid ≈ spent. If prepaid kWh is unknown
      // and nothing is charged yet, keep the full hold visible until metrics arrive.
      if (!periodKwt && chargedKwh <= 0) {
        remainingCents += paidCents;
      }
      continue;
    }

    const spentCents = Math.round((paidCents * chargedKwh) / periodKwt);
    remainingCents += Math.max(0, paidCents - spentCents);
  }

  return remainingCents;
}

/**
 * Header balance during charging: existing wallet + prepaid left (paid - spent).
 */
export function getDisplayBalanceCents(freshBalanceCents, historyData, stationStatus, stationNumber) {
  const existingCents = Number(freshBalanceCents) || 0;
  const prepaidRemainingCents = getInProgressPrepaidRemainingCents(historyData, stationStatus, stationNumber);
  return existingCents + prepaidRemainingCents;
}
